/**
 * InventoryChart Component - Phase 5 Implementation
 * Pie chart for inventory stock distribution by category
 * Shows inventory composition with value breakdown
 */

import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig
} from '../../ui/chart';
import { Skeleton } from '../../ui/skeleton';
import { Package, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface InventoryChartProps {
  data: any[];
  isLoading?: boolean;
  error?: string;
  className?: string;
}

const chartConfig = {
  electronics: {
    label: "Electronics",
    color: "hsl(var(--chart-1))",
  },
  clothing: {
    label: "Clothing",
    color: "hsl(var(--chart-2))",
  },
  books: {
    label: "Books",
    color: "hsl(var(--chart-3))",
  },
  home: {
    label: "Home & Garden",
    color: "hsl(var(--chart-4))",
  },
  sports: {
    label: "Sports",
    color: "hsl(var(--chart-5))",
  },
} satisfies ChartConfig;

// Mock data for demonstration
const generateMockData = () => [
  { 
    item: 'Electronics', 
    category: 'electronics',
    quantity: 850, 
    value: 425000, 
    status: 'good',
    percentage: 35.2
  },
  { 
    item: 'Clothing', 
    category: 'clothing',
    quantity: 1200, 
    value: 240000, 
    status: 'good',
    percentage: 19.8
  },
  { 
    item: 'Books', 
    category: 'books',
    quantity: 2100, 
    value: 157500, 
    status: 'good',
    percentage: 13.0
  },
  { 
    item: 'Home & Garden', 
    category: 'home',
    quantity: 640, 
    value: 256000, 
    status: 'low',
    percentage: 21.1
  },
  { 
    item: 'Sports', 
    category: 'sports',
    quantity: 320, 
    value: 132000, 
    status: 'critical',
    percentage: 10.9
  },
];

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'good':
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'low':
      return <Clock className="h-4 w-4 text-yellow-600" />;
    case 'critical':
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    default:
      return <Package className="h-4 w-4 text-muted-foreground" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'good':
      return 'text-green-600 bg-green-50 border-green-200';
    case 'low':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'critical':
      return 'text-red-600 bg-red-50 border-red-200';
    default:
      return 'text-muted-foreground bg-muted border-border';
  }
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[200px]">
        <p className="font-medium mb-2">{data.item}</p>
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Quantity:</span>
            <span className="font-medium">{data.quantity.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Value:</span>
            <span className="font-medium">{formatCurrency(data.value)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Percentage:</span>
            <span className="font-medium">{data.percentage.toFixed(1)}%</span>
          </div>
          <div className="flex justify-between text-sm items-center pt-1">
            <span className="text-muted-foreground">Status:</span>
            <div className="flex items-center space-x-1">
              {getStatusIcon(data.status)}
              <span className="font-medium capitalize">{data.status}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({
  cx, cy, midAngle, innerRadius, outerRadius, percent
}: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      fontSize={12}
      fontWeight="500"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export const InventoryChart: React.FC<InventoryChartProps> = ({
  data,
  isLoading = false,
  error,
  className
}) => {
  // Use mock data if no data provided or in loading state
  const chartData = data.length > 0 ? data : generateMockData();

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <p className="text-destructive font-medium">Failed to load chart</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-32" />
          <div className="flex space-x-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
        <div className="flex space-x-4">
          <Skeleton className="h-64 w-64 rounded-full" />
          <div className="flex-1 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Calculate summary metrics
  const totalItems = chartData.reduce((sum, item) => sum + item.quantity, 0);
  const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);
  const categories = chartData.length;
  const lowStockItems = chartData.filter(item => item.status !== 'good').length;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 dark:text-blue-300">Total Items</p>
              <p className="text-xl font-bold text-blue-800 dark:text-blue-200">
                {totalItems.toLocaleString()}
              </p>
            </div>
            <div className="text-blue-600">
              <Package className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 dark:text-green-300">Total Value</p>
              <p className="text-xl font-bold text-green-800 dark:text-green-200">
                {formatCurrency(totalValue)}
              </p>
            </div>
            <div className="text-green-600">
              <CheckCircle className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Categories</p>
              <p className="text-xl font-bold">
                {categories}
              </p>
            </div>
            <div className="text-muted-foreground">
              <Package className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className={cn(
          "rounded-lg p-4 border",
          lowStockItems > 0 
            ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"
            : "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
        )}>
          <div className="flex items-center justify-between">
            <div>
              <p className={cn(
                "text-sm",
                lowStockItems > 0 
                  ? "text-red-700 dark:text-red-300"
                  : "text-green-700 dark:text-green-300"
              )}>
                Low Stock
              </p>
              <p className={cn(
                "text-xl font-bold",
                lowStockItems > 0 
                  ? "text-red-800 dark:text-red-200"
                  : "text-green-800 dark:text-green-200"
              )}>
                {lowStockItems}
              </p>
            </div>
            <div className={cn(
              lowStockItems > 0 ? "text-red-600" : "text-green-600"
            )}>
              {lowStockItems > 0 ? (
                <AlertTriangle className="h-6 w-6" />
              ) : (
                <CheckCircle className="h-6 w-6" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Distribution */}
      <div className="flex items-start space-x-6">
        {/* Pie Chart */}
        <div className="flex-1">
          <ChartContainer config={chartConfig} className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>

        {/* Legend & Details */}
        <div className="w-80 space-y-3">
          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Inventory Breakdown
          </h4>
          {chartData.map((item, index) => (
            <div 
              key={item.item} 
              className="flex items-center justify-between p-3 rounded-lg border bg-muted/20"
            >
              <div className="flex items-center space-x-3">
                <div 
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <div>
                  <p className="font-medium text-sm">{item.item}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    {getStatusIcon(item.status)}
                    <span className={cn(
                      "text-xs px-2 py-1 rounded border capitalize",
                      getStatusColor(item.status)
                    )}>
                      {item.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-sm">{formatCurrency(item.value)}</p>
                <p className="text-xs text-muted-foreground">
                  {item.quantity.toLocaleString()} items
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};